import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getProyectosApiBaseUrl } from "../../utils/proyectosApiBaseUrl";

type Proyecto = {
  id: number;
  codigo: string;
  nombre: string;
  solicitante: string | null;
  municipio: string | null;
  estado_global: string | null;
  solicitud_decision: string | null;
  updated_at: string;
};

type Detalle = {
  proyecto: Proyecto | null;
  etapas: Array<{
    id: number;
    etapa_key?: string;
    etapa_nombre: string;
    estado: string;
    porcentaje: number;
    observaciones?: string | null;
  }>;
  documentos: Array<{ id: number; nombre_original: string; etapa_key: string }>;
};

const getToken = () => {
  const encoded = localStorage.getItem("authToken");
  return encoded ? atob(encoded) : "";
};

const labels: Record<string, string> = {
  codigo: "Codigo",
  nombre: "Nombre del proyecto",
  tipo_proyecto: "Tipo de proyecto",
  nivel_tension: "Nivel de tension",
  fecha_estimada_operacion: "Fecha estimada de operacion",
  numero_usuarios: "Numero de usuarios",
  carga_total_solicitada_kva: "Carga total solicitada (kVA)",
  tipo_subestacion: "Tipo de subestacion",
  nodo_referencia: "Nodo de referencia",
  nombre_solicitante: "Nombre del solicitante",
  tipo_documento: "Tipo de documento",
  documento_solicitante: "Documento",
  tipo_persona: "Tipo de persona",
  tipo_persona_juridica: "Tipo de persona juridica",
  correo_notificacion: "Correo",
  telefono_solicitante: "Celular",
  telefono_fijo: "Telefono fijo",
  predio_departamento: "Departamento del predio",
  predio_municipio: "Municipio del predio",
  predio_comuna: "Comuna del predio",
  direccion_predio: "Direccion del predio",
  observaciones: "Observaciones",
  responsable_emcali: "Responsable EMCALI",
  fecha_radicado: "Fecha de solicitud",
};

export default function TablaProyectos() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [proyectos, setProyectos] = React.useState<Proyecto[]>([]);
  const [busquedaProyecto, setBusquedaProyecto] = React.useState("");
  const [detalle, setDetalle] = React.useState<Detalle | null>(null);
  const [detalleLoading, setDetalleLoading] = React.useState(false);
  const [detalleModalOpen, setDetalleModalOpen] = React.useState(false);
  const baseUrl = getProyectosApiBaseUrl();
  const token = React.useMemo(() => getToken(), []);

  const loadProyectos = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${baseUrl}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("No fue posible listar proyectos.");
      const data = await response.json();
      setProyectos(data);
    } catch (err: any) {
      setError(err.message || "Error consultando proyectos.");
    } finally {
      setLoading(false);
    }
  }, [baseUrl, token]);

  const verDetalle = async (id: number) => {
    try {
      setDetalleModalOpen(true);
      setDetalleLoading(true);
      setDetalle(null);
      const response = await fetch(`${baseUrl}/${id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error("No fue posible cargar el detalle.");
      const data = await response.json();
      setDetalle(data);
    } catch (err: any) {
      setError(err.message || "Error cargando detalle.");
    } finally {
      setDetalleLoading(false);
    }
  };

  React.useEffect(() => {
    loadProyectos();
  }, [loadProyectos]);

  const proyectosFiltrados = React.useMemo(() => {
    const q = busquedaProyecto.trim().toLowerCase();
    if (!q) return proyectos;
    return proyectos.filter(
      (p) =>
        (p.codigo || "").toLowerCase().includes(q) ||
        (p.nombre || "").toLowerCase().includes(q),
    );
  }, [proyectos, busquedaProyecto]);

  const detalleFormulario = React.useMemo(() => {
    const raw = detalle?.etapas?.find((e: any) => e.etapa_key === "solicitud_servicio")?.observaciones;
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.formulario && typeof parsed.formulario === "object") return parsed.formulario;
      if (typeof parsed === "object") return parsed;
      return null;
    } catch {
      return null;
    }
  }, [detalle]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Gestion de Proyectos</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" color="success" onClick={() => navigate("/proyectos-panel/nuevo")}>
            Crear proyecto
          </Button>
          <Button variant="contained" onClick={loadProyectos}>Refrescar proyectos</Button>
        </Stack>
      </Stack>
      <TextField
        size="small"
        fullWidth
        label="Buscar por codigo o nombre del proyecto"
        value={busquedaProyecto}
        onChange={(e) => setBusquedaProyecto(e.target.value)}
        sx={{ mb: 2 }}
      />

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <CircularProgress size={20} />
          <Typography>Cargando listado...</Typography>
        </Stack>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Codigo</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Solicitante</TableCell>
              <TableCell>Municipio</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Decision</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {proyectosFiltrados.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>{p.id}</TableCell>
                <TableCell>{p.codigo || "-"}</TableCell>
                <TableCell>{p.nombre || "-"}</TableCell>
                <TableCell>{p.solicitante || "-"}</TableCell>
                <TableCell>{p.municipio || "-"}</TableCell>
                <TableCell><Chip size="small" label={p.estado_global || "-"} /></TableCell>
                <TableCell>{p.solicitud_decision || "-"}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => navigate(`/proyectos-panel/${p.id}/gestion`)}
                    >
                      Gestion
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => verDetalle(p.id)}>
                      Ver detalle
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {proyectosFiltrados.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No hay proyectos que coincidan con la busqueda.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog
        open={detalleModalOpen}
        onClose={() => setDetalleModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Detalle del proyecto</DialogTitle>
        <DialogContent>
          {detalleLoading && <Typography>Cargando detalle...</Typography>}
          {!detalleLoading && !detalle?.proyecto && (
            <Typography color="text.secondary">No se pudo cargar el detalle del proyecto.</Typography>
          )}
          {detalle?.proyecto && (
            <Stack spacing={1.2}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <img src="/images/logo-emcali.webp" alt="Logo EMCALI" style={{ height: 32, width: "auto" }} />
                  <Typography variant="h6" fontWeight={700}>Detalle del proyecto</Typography>
                </Stack>
                <Chip size="small" label={detalle.proyecto.estado_global || "-"} />
              </Stack>
              <Grid container spacing={1}>
                {Object.entries(detalleFormulario || {}).map(([key, value]) => {
                  const text = String(value ?? "").trim();
                  if (!text) return null;
                  return (
                    <Grid item xs={12} md={6} key={key}>
                      <Paper variant="outlined" sx={{ p: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {labels[key] || key}
                        </Typography>
                        <Typography variant="body2">{text}</Typography>
                      </Paper>
                    </Grid>
                  );
                })}
                {!detalleFormulario && (
                  <>
                    <Grid item xs={12} md={6}><Typography><strong>Proyecto:</strong> {detalle.proyecto.nombre}</Typography></Grid>
                    <Grid item xs={12} md={6}><Typography><strong>Codigo:</strong> {detalle.proyecto.codigo}</Typography></Grid>
                    <Grid item xs={12} md={6}><Typography><strong>Solicitante:</strong> {detalle.proyecto.solicitante || "-"}</Typography></Grid>
                    <Grid item xs={12} md={6}><Typography><strong>Estado:</strong> {detalle.proyecto.estado_global || "-"}</Typography></Grid>
                    <Grid item xs={12} md={6}><Typography><strong>Etapas:</strong> {detalle.etapas.length}</Typography></Grid>
                    <Grid item xs={12} md={6}><Typography><strong>Documentos:</strong> {detalle.documentos.length}</Typography></Grid>
                  </>
                )}
              </Grid>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
